import React, { useState } from "react";
import { Modal, Box, Typography, TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { toast } from "react-toastify";
import { ContentCopy, Visibility, VisibilityOff } from "@mui/icons-material";

import "react-toastify/dist/ReactToastify.css";
import SuperAdminUserService from './../../../service/superadmin/userService';

interface UpdateUserPasswordProps {
    open: boolean;
    onClose: () => void;
    userId: number;
}

const UpdateUserPassword: React.FC<UpdateUserPasswordProps> = ({ open, onClose, userId }) => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // Function to generate a random password
    const generatePassword = () => {
        const randomPassword = Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-2);
        setNewPassword(randomPassword);
        setConfirmPassword(randomPassword);
    };

    // Function to copy the password to clipboard with validation
    const copyPassword = () => {
        if (!newPassword) {
            toast.error("No password to copy!");
            return;
        }
        navigator.clipboard.writeText(newPassword);
        toast.info("Password copied to clipboard!");
    };

    const handleSubmit = async () => {
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            toast.error("Passwords do not match.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            await SuperAdminUserService.updateUserPassword(userId, newPassword);
            toast.success("Password updated successfully!");
            setConfirmPassword('');
            setNewPassword('');
            onClose();
        } catch (error) {
            toast.error("Failed to update password.");
            setError("Failed to update password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="update-password-modal">
            <Box sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 600,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2
            }}>
                <Typography id="update-password-modal" variant="h6" component="h2">
                    Update Password
                </Typography>

                <TextField
                    fullWidth
                    margin="normal"
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={copyPassword}>
                                    <ContentCopy />
                                </IconButton>
                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Confirm Password"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                    <Button variant="contained" color="info" onClick={generatePassword} disabled={loading}>
                        Generate Password
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Updating..." : "Update Password"}
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default UpdateUserPassword;
